import { Link } from "react-router-dom";
import { ArrowUpRight, Monitor, Globe2, Sparkles, Compass } from "lucide-react";
import Reveal from "@/components/Reveal";

const IMG_BASE = "/images/corso-base.jpg";
const IMG_WEB = "/images/web-design.jpg";
const IMG_LEVEL = "/images/livello.jpg";

const SPECIALIZZAZIONI = ["AutoCAD", "Adobe Photoshop", "Animazione 3D", "Corel Draw", "Grafica pubblicitaria", "Video editing"];

const card =
  "group relative overflow-hidden rounded-3xl border border-[#D8E5E8] bg-white shadow-[0_10px_40px_-24px_rgba(16,51,59,0.25)] transition-colors duration-500 hover:border-[#1F8296]/50";

export default function CourseBentoGrid() {
  return (
    <section className="relative mx-auto max-w-7xl px-5 py-24 sm:px-8" data-testid="course-bento">
      <Reveal>
        <p className="font-plex text-xs uppercase tracking-[0.28em] text-[#1F8296]">L'offerta formativa</p>
        <h2 className="text-balance mt-4 max-w-2xl font-display text-3xl font-semibold tracking-tight text-[#1A2E33] sm:text-4xl">
          Tre percorsi, un solo scopo: il tuo obiettivo
        </h2>
      </Reveal>

      <div className="mt-12 grid grid-cols-1 gap-5 lg:grid-cols-12">
        <Reveal className="lg:col-span-7" delay={0.05}>
          <article className={`${card} flex h-full flex-col`} data-testid="bento-corso-base">
            <div className="relative h-60 overflow-hidden sm:h-72">
              <img
                src={IMG_BASE}
                alt="Studente al computer durante il corso base"
                className="h-full w-full object-cover transition-transform duration-700 ease-out group-hover:scale-105"
                loading="lazy"
              />
            </div>
            <div className="flex flex-1 flex-col p-7 sm:p-9">
              <div className="flex items-center gap-3">
                <Monitor className="h-5 w-5 text-[#1F8296]" />
                <h3 className="font-display text-2xl font-semibold text-[#1A2E33]">Corso Base di Informatica</h3>
              </div>
              <p className="mt-4 text-sm leading-relaxed text-[#5C7178] sm:text-base">
                Per chi non sa niente — o quasi — del computer. Informatica di base, telematica e
                internet, office automation, progettazione web, elaborazione digitale delle immagini,
                multimedialità e hardware. Per diventare finalmente indipendente e sicuro nell'uso del PC.
              </p>
              <Link to="/corsi" data-testid="bento-base-link" className="mt-auto inline-flex items-center gap-1.5 pt-6 text-sm font-semibold text-[#1F8296] transition-colors hover:text-[#238496]">
                Scopri il percorso <ArrowUpRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
              </Link>
            </div>
          </article>
        </Reveal>

        <Reveal className="lg:col-span-5" delay={0.12}>
          <article className={`${card} flex h-full flex-col`} data-testid="bento-web-design">
            <div className="relative h-44 overflow-hidden">
              <img
                src={IMG_WEB}
                alt="Un allievo senior al lavoro sul laptop in aula"
                className="h-full w-full object-cover transition-transform duration-700 ease-out group-hover:scale-105"
                loading="lazy"
              />
            </div>
            <div className="flex flex-1 flex-col p-7">
              <div className="flex items-center gap-3">
                <Globe2 className="h-5 w-5 text-[#1F8296]" />
                <h3 className="font-display text-2xl font-semibold text-[#1A2E33]">Web Design</h3>
              </div>
              <p className="mt-4 text-sm leading-relaxed text-[#5C7178]">
                Prototipazione e progettazione di siti web dinamici con le tecnologie più attuali:
                il percorso per specializzarsi nel mondo del Web.
              </p>
            </div>
          </article>
        </Reveal>

        <Reveal className="lg:col-span-5" delay={0.05}>
          <article className={`${card} flex h-full flex-col p-7 sm:p-9`} data-testid="bento-specializzazioni">
            <div className="flex items-center gap-3">
              <Sparkles className="h-5 w-5 text-[#1F8296]" />
              <h3 className="font-display text-2xl font-semibold text-[#1A2E33]">Specializzazioni</h3>
            </div>
            <p className="mt-4 text-sm leading-relaxed text-[#5C7178]">
              Corsi di specializzazione specifici per gli argomenti avanzati più richiesti dal mercato.
            </p>
            <div className="mt-6 flex flex-wrap gap-2.5">
              {SPECIALIZZAZIONI.map((s) => (
                <span
                  key={s}
                  className="rounded-full border border-[#C9DBDF] bg-[#EFF6F7] px-4 py-2 text-xs font-semibold text-[#33484F] transition-colors duration-300 hover:border-[#1F8296]/50 hover:text-[#1F8296]"
                >
                  {s}
                </span>
              ))}
            </div>
            <Link to="/corsi" data-testid="bento-spec-link" className="mt-auto inline-flex items-center gap-1.5 pt-7 text-sm font-semibold text-[#1F8296] transition-colors hover:text-[#238496]">
              Tutte le specializzazioni <ArrowUpRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
            </Link>
          </article>
        </Reveal>

        <Reveal className="lg:col-span-7" delay={0.12}>
          <article className="group relative flex h-full min-h-[280px] flex-col justify-end overflow-hidden rounded-3xl border border-[#0C2930] p-7 sm:p-9" data-testid="bento-livello">
            <img
              src={IMG_LEVEL}
              alt="Docente affianca gli studenti in laboratorio"
              className="absolute inset-0 h-full w-full object-cover opacity-40 transition-transform duration-700 ease-out group-hover:scale-105"
              loading="lazy"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-[#0C2930] via-[#0C2930]/60 to-transparent" />
            <div className="relative">
              <div className="flex items-center gap-3">
                <Compass className="h-5 w-5 text-[#5FC6D6]" />
                <h3 className="font-display text-2xl font-semibold text-white">Valutiamo il tuo livello</h3>
              </div>
              <p className="mt-4 max-w-lg text-sm leading-relaxed text-[#CFE3E8] sm:text-base">
                Insieme valuteremo le tue conoscenze, le tue aspettative e i tuoi desideri, per
                proporti esattamente quello che stai cercando e prefiggerti il tuo prossimo obiettivo.
              </p>
              <Link
                to="/contatti"
                data-testid="bento-livello-cta"
                className="mt-6 inline-flex items-center gap-2 rounded-full bg-[#2997AB] px-6 py-3 text-sm font-bold text-white transition-colors duration-300 hover:bg-[#2FA7BC]"
              >
                Chiedi a noi <ArrowUpRight className="h-4 w-4" />
              </Link>
            </div>
          </article>
        </Reveal>
      </div>
    </section>
  );
}
